import React, { useEffect, useState } from 'react';
import { View, Pressable, Linking, StyleSheet } from 'react-native';
import Text from '@/components/Typography/Text';
import { API_HOST } from '@/constants/config';
import { getSnapshot, subscribe, receiveSiteAnnouncement, visibleAnnouncement } from '@/lib/siteAnnouncements';

const DISMISS_KEY = 'gab.site-announcement.dismissed';
const storedDismissal = () => { try { return window.localStorage.getItem(DISMISS_KEY); } catch { return null; } };

export default function SiteAnnouncementBanner() {
  const [state, setState] = useState(getSnapshot);
  const [dismissed, setDismissed] = useState(storedDismissal);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const unsubscribe = subscribe(() => setState(getSnapshot()));
    setState(getSnapshot());
    return unsubscribe;
  }, []);
  useEffect(() => {
    let stopped = false;
    let controller;
    const refresh = async () => {
      if (controller) return;
      controller = new AbortController();
      const current = controller;
      const timeout = setTimeout(() => current.abort(), 8000);
      try {
        const response = await fetch(`${API_HOST}/api/site-announcement`, { cache: 'no-store', signal: current.signal });
        if (response.ok) {
          const data = await response.json();
          if (!stopped) receiveSiteAnnouncement(data);
        }
      } catch { /* Keep existing notice until its own expiry during a deploy. */ }
      finally { clearTimeout(timeout); controller = null; }
    };
    const resume = () => { setNow(Date.now()); if (document.visibilityState === 'visible') refresh(); };
    const storage = event => { if (event.key === DISMISS_KEY) setDismissed(event.newValue); };
    refresh();
    const poll = setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('storage', storage);
    return () => { stopped = true; controller?.abort(); clearInterval(poll);
      document.removeEventListener('visibilitychange', resume); window.removeEventListener('storage', storage); };
  }, []);
  useEffect(() => {
    if (!state.announcement) return;
    let timer;
    const tick = () => {
      const current = Date.now();
      setNow(current);
      const start = Date.parse(state.announcement.startsAt);
      const end = Date.parse(state.announcement.expiresAt);
      const next = start > current ? start : end;
      if (next > current) timer = setTimeout(tick, Math.min(next - current, 2147483000));
    };
    tick();
    return () => clearTimeout(timer);
  }, [state]);
  const announcement = visibleAnnouncement(state, dismissed, now);
  if (!announcement) return null;
  const palette = { info: ['#e9f1ff', '#173d76'], warning: ['#fff2ce', '#614600'], success: ['#e4f5e9', '#185b30'] }[announcement.severity] || ['#e9f1ff', '#173d76'];
  const dismiss = () => {
    setDismissed(announcement.id);
    try { window.localStorage.setItem(DISMISS_KEY, announcement.id); } catch { /* In-memory dismissal still works. */ }
  };
  const validLink = /^https:\/\//i.test(announcement.linkUrl || '');
  return (
    <View accessibilityLiveRegion="polite" style={[styles.banner, { backgroundColor: palette[0] }]}>
      <View style={styles.content}>
        <Text style={{ color: palette[1], fontWeight: '700' }}>{announcement.title}</Text>
        <Text style={{ color: palette[1] }}>{announcement.message}</Text>
        {validLink && <Pressable accessibilityRole="link" onPress={() => Linking.openURL(announcement.linkUrl).catch(() => {})}>
          <Text style={{ color: palette[1], textDecorationLine: 'underline' }}>{announcement.linkLabel || 'More information'}</Text>
        </Pressable>}
      </View>
      <Pressable accessibilityRole="button" accessibilityLabel="Dismiss announcement" onPress={dismiss} style={styles.close}>
        <Text style={{ color: palette[1], fontSize: 24 }}>×</Text>
      </Pressable>
    </View>
  );
}
const styles = StyleSheet.create({
  banner: { flexDirection: 'row', alignItems: 'center', paddingLeft: 16, paddingVertical: 10, width: '100%', flexShrink: 0 },
  content: { flex: 1, gap: 3 },
  close: { minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center', marginHorizontal: 6 },
});
